import styled from '@emotion/styled';
import { colors, fonts, layout, DESKTOP } from './tokens';

/* ── Page shell ──────────────────────────────────────────────────────────── */

export const Page = styled.div`
  background: ${colors.pageBg};
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

export const TopNavBar = styled.div`
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 21px;
  flex-shrink: 0;
    ${DESKTOP} {
   height: ${layout.topNavHeight}px;

  }
`;

// Mobile: content is a 353px column. Desktop: a wider centred container whose
// inner blocks stay left-aligned (per Desktop-Experience.png).
export const Content = styled.div`
  width: 100%;
  max-width: ${layout.contentWidth}px;
  margin: 0 auto;
  margin-bottom: 100px;
  padding: 60px 0 24px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  flex: 1 1 auto;

  ${DESKTOP} {
    max-width: ${layout.desktopMaxWidth}px;
    padding: 40px 40px 24px;
    gap: 28px;
  }
`;

export const Heading = styled.h1`
  font-family: ${fonts.heading};
  font-weight: 400;
  font-size: 30px;
  line-height: 1.2;
  letter-spacing: -0.18px;
  color: ${colors.heading};
  margin: 0;
`;

/* ── "Your purchased add-on(s)" block ───────────────────────────────────── */

export const SectionLabel = styled.p`
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: 0.96px;
  text-transform: uppercase;
  color: ${colors.muted};
  margin: 0;
`;

export const SectionPrompt = styled.p`
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: -0.096px;
  color: ${colors.muted};
  margin: 0;
`;

export const MutedText = styled.p`
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: -0.096px;
  color: ${colors.muted};
  margin: 0;
`;

export const ChipsBox = styled.div`
  background: ${colors.chipsPanelBg};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  padding: 20px;
  width: 100%;
`;

export const ChipsPanel = styled.div`
  background: ${colors.chipsPanelBg};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  background: ${colors.cardBg};

  box-shadow: 4px 4px 4px 0 rgba(133, 79, 155, 0.25);
  ${DESKTOP} {
    flex-direction: row;
    flex-wrap: wrap;
    padding: 6px 10px;
    /* Max 3 add-on columns on desktop. */
    > * {
      flex: 0 0 calc(33.333% - 8px);
      min-width: 220px;
    }
  }
`;

export const SelectableChip = styled.button`
  appearance: none;
  background: none;
  border: none;
  text-align: left;
  display: flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 9999px;
  width: 100%;
  cursor: pointer;
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: -0.096px;
  color: ${colors.text};

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

// Figma: 20px box, 1.5px #D1D1D1 border, radius 1px, inside a 60px-wide holder
// that produces the chip's left indent.
export const CheckboxHolder = styled.span`
  width: 60px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const CheckboxBox = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 1px;
  border: 1.5px solid ${props => (props.checked ? colors.brand : colors.chipBorder)};
  background: ${props => (props.checked ? colors.brand : '#fff')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
`;

/* ── Tabs ────────────────────────────────────────────────────────────────── */

// Full-bleed on mobile (the underline track spans the whole 412px frame).
export const TabsWrap = styled.div`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${DESKTOP} {
    width: 100%;
    margin-left: 0;
  }
`;

export const TabsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 20px;
  padding: 0 30px;

  ${DESKTOP} {
    justify-content: flex-start;
    gap: 40px;
    padding: 0;
  }
`;

export const Tab = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: -0.096px;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${props => (props.active ? colors.activeTab : colors.muted)};
`;

export const TabTrack = styled.div`
  position: relative;
  height: 2px;
  background: ${colors.tabTrack};
  width: 100%;
`;

export const TabIndicator = styled.div`
  position: absolute;
  top: 0;
  height: 2px;
  background: ${colors.activeTab};
  left: ${props => props.left};
  width: ${props => props.width};
  transition: left 150ms ease, width 150ms ease;
`;

/* ── Cards ───────────────────────────────────────────────────────────────── */

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;

  ${DESKTOP} {
    grid-template-columns: repeat(3, 1fr);
    align-items: start;
  }
`;

// Unassigned book tile — radius 10, border #E5DAF6, pt12/pb14/px16.
export const UnassignedCard = styled.div`
  background: ${colors.cardBg};
  border: 1px solid ${colors.unassignedCardBorder};
  border-radius: 10px;
  padding: 12px 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

// My Books / Gifts tile — radius 16, border #D1D1D1, p16.
export const JournalCard = styled.div`
  background: ${colors.cardBg};
  border: 1px solid ${colors.journalCardBorder};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const BundleName = styled.p`
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: -0.084px;
  color: ${colors.muted};
  margin: 0;
`;

export const CardTitle = styled.p`
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 18px;
  line-height: 1.5;
  letter-spacing: -0.108px;
  color: ${colors.text};
  margin: 0;
`;

export const SpecText = styled.p`
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: -0.084px;
  color: ${colors.muted};
  margin: 0;
`;

export const DetailsLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: -0.084px;
  color: ${colors.brand};
  white-space: nowrap;
  flex-shrink: 0;
`;

export const SpecRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
`;

/* ── Entitlement chips on a card ─────────────────────────────────────────── */

// `pending` = held in local state only (yellow). Otherwise = persisted by the
// API (white). Both share radius 9999px / 1px #D1D1D1 per the design.
export const EntitlementChip = styled.div`
  background: ${props => (props.pending ? colors.pendingBg : colors.cardBg)};
  border: 1px solid ${colors.chipBorder};
  border-radius: 9999px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: ${props => (props.pending ? 'space-between' : 'flex-start')};
  gap: 10px;
  width: ${props => (props.pending ? '100%' : 'auto')};
  align-self: ${props => (props.pending ? 'stretch' : 'flex-start')};
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: -0.084px;
  color: ${colors.text};
`;

export const RemoveButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: -0.084px;
  color: ${colors.text};
  white-space: nowrap;
`;

/* ── Buttons ─────────────────────────────────────────────────────────────── */

const buttonBase = `
  height: 44px;
  border-radius: 23px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: -0.096px;
  white-space: nowrap;
`;

export const PrimaryButton = styled.button`
  ${buttonBase};
  background: ${colors.brand};
  border: 1px solid ${colors.brand};
  color: #fff;
  width: ${props => (props.inline ? 'auto' : '100%')};

  &:disabled {
    background: ${colors.disabled};
    border-color: ${colors.disabled};
    cursor: default;
  }
`;

export const OutlineButton = styled.button`
  ${buttonBase};
  background: none;
  border: 1.5px solid ${props => (props.disabled ? colors.disabled : colors.brand)};
  color: ${props => (props.disabled ? colors.disabled : colors.brand)};
  width: ${props => (props.inline ? 'auto' : '100%')};

  &:disabled {
    cursor: default;
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;

  > * {
    flex: 1 1 0;
    min-width: 0;
  }
`;

/* ── Empty states ────────────────────────────────────────────────────────── */

export const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

export const EmptyIllustration = styled.img`
  height: 150px;
  width: auto;
  opacity: 0.7;
  pointer-events: none;
`;

export const EmptyCopy = styled.p`
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: -0.096px;
  color: ${colors.muted};
  text-align: center;
  margin: 0;
`;

export const EmptyAction = styled.div`
  padding-top: 10px;
  width: 100%;

  ${DESKTOP} {
    max-width: 353px;
  }
`;

/* ── Bottom nav (mobile) / footer bars ───────────────────────────────────── */

export const BottomNav = styled.div`
  background: #fff;
  border-top: 1.5px solid ${colors.bottomNavBorder};
  min-height: ${layout.bottomNavHeight}px;
  padding: 17px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
  position: sticky;
  bottom: 0;

  ${DESKTOP} {
    display: none;
  }
`;

export const NavAction = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: -0.096px;
  color: ${colors.muted};
  white-space: nowrap;
`;

// Add-ons mode footer: Back on the left, Confirm on the right.
export const ConfirmBar = styled.div`
  background: #fff;
  border-top: 1.5px solid ${colors.bottomNavBorder};
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  bottom: 0;
  ${DESKTOP} {
    padding: 12px 30px;
  }
`;

export const BackButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 16px;
  line-height: 1.2;
  letter-spacing: -0.352px;
  color: ${colors.backText};
`;

// "+ Add-ons" entry point under a book / gift card's primary action — opens the
// in-app purchase shelf for that journal.
export const AddOnsLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 4px 0 0;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: -0.084px;
  color: ${colors.muted};
`;

export const ErrorText = styled.p`
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  color: #D8000C;
  margin: 0;
`;

/* ── Desktop-only utility nav in the top bar ─────────────────────────────── */

export const TopNavActions = styled.div`
  display: none;

  ${DESKTOP} {
    display: flex;
    align-items: center;
    gap: 30px;
  }
`;
